import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Icon } from '@/components/ui/Icon'
import { Avatar } from '@/components/ui/Avatar'
import { Spinner } from '@/components/ui/Spinner'
import { ThemedSelect } from '@/components/ui/ThemedSelect'
import { CreateGroupModal } from '@/components/groups/CreateGroupModal'
import { EmojiPickerModal } from '@/components/groups/EmojiPickerModal'
import { ChangelogModal } from '@/components/layout/ChangelogModal'
import { Logo } from '@/components/ui/Logo'
import { useUIStore } from '@/store/ui'
import { useAuthStore } from '@/store/auth'
import { logout as logoutApi } from '@/api/auth'
import { fetchGroupSummary } from '@/api/groups'
import { setChangelogLastSeen } from '@/api/users'
import { getUnreadCount, LATEST_CHANGELOG_VERSION } from '@/data/changelog'
import type { Group, Friend, RolePreview, UserProfile } from '@/types'

const ROLE_PREVIEW_OPTIONS: readonly { value: RolePreview; label: string }[] = [
  { value: 'actual', label: 'My role (actual)' },
  { value: 'creator', label: 'Preview: Creator' },
  { value: 'admin', label: 'Preview: Admin' },
  { value: 'user', label: 'Preview: User' },
  { value: 'voter', label: 'Preview: Voter' },
]

interface SidebarProps {
  groups: Group[]
  groupsLoading: boolean
  currentUser: Friend
  isOpen: boolean
  onClose: () => void
}

export function Sidebar({ groups, groupsLoading, currentUser, isOpen, onClose }: SidebarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { activeGroupId, setActiveGroupId, adminViewEnabled, toggleAdminView, rolePreview, setRolePreview } = useUIStore()
  const { user, logout, setUser } = useAuthStore()
  const queryClient = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [editingEmojiGroup, setEditingEmojiGroup] = useState<Group | null>(null)
  const [showChangelog, setShowChangelog] = useState(false)
  const [unreadAtOpen, setUnreadAtOpen] = useState(0)
  const unreadCount = getUnreadCount(user?.changelogLastSeen)

  function openChangelog() {
    setUnreadAtOpen(unreadCount)
    setShowChangelog(true)
    if (user?.changelogLastSeen === LATEST_CHANGELOG_VERSION) return
    if (user) setUser({ ...user, changelogLastSeen: LATEST_CHANGELOG_VERSION })
    queryClient.setQueryData<UserProfile>(['me'], (prev) =>
      prev ? { ...prev, changelogLastSeen: LATEST_CHANGELOG_VERSION } : prev,
    )
    setChangelogLastSeen(LATEST_CHANGELOG_VERSION).catch(() => {
      queryClient.invalidateQueries({ queryKey: ['me'] })
    })
  }

  const { data: summary } = useQuery({
    queryKey: ['group-summary', activeGroupId],
    queryFn: () => fetchGroupSummary(activeGroupId!),
    enabled: !!activeGroupId,
  })
  const NAV_ITEMS = [
    { path: '/wishlist',  label: 'Wishlist',         icon: 'wishlist'   as const, count: summary?.wishlistCount },
    { path: '/playing',   label: 'Now Playing',      icon: 'controller' as const, count: summary?.playingCount  },
    { path: '/played',    label: 'Played & Rated',   icon: 'star'       as const, count: summary?.playedCount   },
    { path: '/common-library', label: 'Common Library', icon: 'friends'  as const },
    { path: '/recs',      label: 'Recommendations',  icon: 'lightbulb'  as const },
  ]

  function go(path: string) {
    navigate(path)
    onClose()
  }

  async function handleLogout() {
    try { await logoutApi() } catch { /* ignore */ }
    logout()
    navigate('/login', { replace: true })
  }

  const isGroupSection = !location.pathname.startsWith('/library') && !location.pathname.startsWith('/friends')

  return (
    <aside className="cg-sidebar" data-open={isOpen}>
      <div className="cg-brand">
        <Logo size={24} />
        <span>Common Ground</span>
      </div>

      <div className="cg-sidebar-scroll">
        <div
          className="cg-nav-section"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 8 }}
        >
          Groups          <button
            className="cg-icon-btn"
            style={{ width: 20, height: 20, fontSize: 16 }}
            onClick={() => setShowCreate(true)}
            title="New group"
          >
            +
          </button>
        </div>
        {showCreate && <CreateGroupModal onClose={() => setShowCreate(false)} />}
        {editingEmojiGroup && (
          <EmojiPickerModal
            groupId={editingEmojiGroup.id}
            currentEmoji={editingEmojiGroup.emoji}
            onClose={() => setEditingEmojiGroup(null)}
          />
        )}
        {groupsLoading && (
          <Spinner size={16} wrapClassName="cg-spinner-wrap-sidebar" />
        )}
        {groups.map((g) => (
          <div
            key={g.id}
            className="cg-nav-item"
            data-active={isGroupSection && activeGroupId === g.id}
            onClick={() => {
              setActiveGroupId(g.id)
              if (!isGroupSection) navigate('/wishlist')
              onClose()
            }}
          >
            <span
              className="cg-nav-emoji"
              title="Change icon"
              onClick={(e) => {
                e.stopPropagation()
                setEditingEmojiGroup(g)
              }}
            >
              {g.emoji}
            </span>
            <span className="cg-nav-label">{g.name}</span>
            <span className="cg-nav-meta">{g.members.length}</span>
          </div>
        ))}

        <div className="cg-nav-section">Navigate</div>
        {NAV_ITEMS.map((item) => (
          <div
            key={item.path}
            className="cg-nav-item"
            data-active={location.pathname === item.path}
            onClick={() => go(item.path)}
          >
            <span className="cg-nav-icon"><Icon name={item.icon} size={14} /></span>
            <span className="cg-nav-label">{item.label}</span>
            {item.count != null && item.count > 0 && (
              <span className="cg-nav-meta">{item.count}</span>
            )}
          </div>
        ))}

        <div className="cg-nav-section">My account</div>
        <div
          className="cg-nav-item"
          data-active={location.pathname === '/library'}
          onClick={() => go('/library')}
        >
          <span className="cg-nav-icon"><Icon name="library" size={14} /></span>
          <span className="cg-nav-label">Steam library</span>
        </div>
        <div
          className="cg-nav-item"
          data-active={location.pathname === '/friends'}
          onClick={() => go('/friends')}
        >
          <span className="cg-nav-icon"><Icon name="friends" size={14} /></span>
          <span className="cg-nav-label">Friends & Groups</span>
        </div>
      </div>

      <div className="cg-sidebar-footer">
        {user?.isAdmin && (
          <button
            className="cg-nav-item"
            style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 4, opacity: adminViewEnabled ? 1 : 0.5 }}
            onClick={toggleAdminView}
            title={adminViewEnabled ? 'Admin view on — click to preview as regular user' : 'Admin view off — click to restore'}
          >
            <span className="cg-nav-icon"><Icon name={adminViewEnabled ? 'eye' : 'eye-off'} size={14} /></span>
            <span className="cg-nav-label">Admin view</span>
            <span className="cg-nav-meta" style={{ fontSize: 10, opacity: 0.7 }}>{adminViewEnabled ? 'on' : 'off'}</span>
          </button>
        )}
        {user?.isAdmin && adminViewEnabled && (
          <div style={{ padding: '0 8px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 10, opacity: 0.6, paddingLeft: 4 }}>Preview role</span>
            <ThemedSelect
              value={rolePreview}
              options={ROLE_PREVIEW_OPTIONS}
              onChange={setRolePreview}
              ariaLabel="Preview a group role"
            />
          </div>
        )}
        <button
          className="cg-nav-item"
          style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 4 }}
          onClick={openChangelog}
        >
          <span className="cg-nav-icon"><Icon name="spark" size={14} /></span>
          <span className="cg-nav-label">What's New</span>
          {unreadCount > 0 && <span className="cg-changelog-badge">{unreadCount}</span>}
        </button>
        {showChangelog && <ChangelogModal onClose={() => setShowChangelog(false)} unreadCount={unreadAtOpen} />}
        <div className="cg-sidebar-user">
          <Avatar friend={currentUser} size={32} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="cg-sidebar-user-name">{currentUser.name}</div>
            <div className="cg-sidebar-user-handle">@{currentUser.handle}</div>
          </div>
          <button
            onClick={handleLogout}
            title="Sign out"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-2)', padding: 4, flexShrink: 0 }}
          >
            <Icon name="logout" size={14} />
          </button>
        </div>
      </div>
    </aside>
  )
}
